import React, { useEffect, useState } from 'react';
import { databaseService } from '../../services/database.service';
import { i18nService } from '../../services/i18n.service';
import AdminLayout from '../../components/layout/AdminLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LanguageTabs from '../../components/common/LanguageTabs';
import toast from 'react-hot-toast';

const PrivacyPolicyManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentLang, setCurrentLang] = useState('nl');
  const [content, setContent] = useState({ nl: '', fr: '', de: '', en: '' });

  useEffect(() => {
    fetchPrivacyPolicy();
  }, []);

  const fetchPrivacyPolicy = async () => {
    try {
      const data = await databaseService.getPageContent('privacyPolicy');
      if (data) {
        setContent(i18nService.extractMultiLangField(data, 'content'));
      }
    } catch (error) {
      console.error('Error fetching privacy policy:', error);
      toast.error('Failed to load privacy policy');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        ...i18nService.setTranslatedFields('content', content),
        lastUpdated: Date.now()
      };
      await databaseService.update('website/pages/privacyPolicy', dataToSave);
      toast.success('Privacy policy saved successfully');
    } catch (error) {
      toast.error('Failed to save privacy policy');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Privacy Policy Manager</h1>
          <p className="text-gray-600">Edit your privacy policy content</p>
        </div>

        <Card>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Privacy Policy Content
            </label>
            <LanguageTabs
              currentLang={currentLang}
              onLangChange={setCurrentLang}
            />
            <textarea
              value={content[currentLang]}
              onChange={(e) => setContent({ ...content, [currentLang]: e.target.value })}
              className="w-full min-h-[500px] p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              placeholder={`Enter your privacy policy content here in ${currentLang.toUpperCase()}. You can use HTML tags for formatting...`}
            />
            <p className="text-sm text-gray-500 mt-2">
              You can use HTML tags for formatting (e.g., &lt;h2&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt;).
              Switch between language tabs to edit content in different languages.
            </p>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Suggested Sections:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Information We Collect</li>
              <li>• How We Use Your Information</li>
              <li>• Cookie Policy</li>
              <li>• Data Security</li>
              <li>• Your Rights (GDPR)</li>
              <li>• International Data Transfers</li>
              <li>• Contact Information</li>
            </ul>
          </div>
        </Card>

        <div className="flex justify-end">
          <Button variant="primary" onClick={handleSave} loading={saving} disabled={saving}>
            {saving ? 'Saving...' : 'Save Privacy Policy'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PrivacyPolicyManager;
